import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation as useRouterLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { LocationProvider, useLocation } from './contexts/LocationContext';
import { ProtectedRoute, RoleRoute } from './components/ProtectedRoute';
import LocationSelectionPopup from './components/LocationSelectionPopup';
import EmergencyServiceButton from './components/EmergencyServiceButton';
import MusicPlayer from './components/MusicPlayer';

// Public pages
import Home from './pages/Home';
import BazarList from './pages/BazarList';
import DailyBazar from './pages/DailyBazar';
import BazarDetail from './pages/BazarDetail';
import StoreDetail from './pages/StoreDetail';
import ProductDetail from './pages/ProductDetail';
import MedicineBazarProductDetail from './pages/MedicineBazarProductDetail';
import FoodBazarProductDetail from './pages/FoodBazarProductDetail';
import ShoshoBazarProductDetail from './pages/ShoshoBazarProductDetail';
import ShoshoBazaar from './pages/ShoshoBazaar';
import FoodBazar from './pages/FoodBazar';
import MedicineBazar from './pages/MedicineBazar';
import NationalBazar from './pages/NationalBazar';
import ServiceBazar from './pages/ServiceBazar';
import BookBazar from './pages/BookBazar';
import ElectronicsBazar from './pages/ElectronicsBazar';
import GarmentsBazar from './pages/GarmentsBazar';
import ChemicalBazar from './pages/ChemicalBazar';
import HardwareBazar from './pages/HardwareBazar';
import CosmeticsBazar from './pages/CosmeticsBazar';
import Auth from './pages/Auth';
import Login from './pages/auth/Login';
import NurseryBazar from './pages/NurseryBazar';
import SearchResult from './pages/SearchResult';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import LocationTest from './pages/LocationTest';
import Help from './pages/Help';
import Contact from './pages/Contact';
import Career from './pages/Career';
import JobDetail from './pages/JobDetail';
import About from './pages/About';
import BecomeSeller from './pages/BecomeSeller';
import MarketplacePolicy from './pages/MarketplacePolicy';
import DeliveryPolicy from './pages/DeliveryPolicy';
import ReturnPolicy from './pages/ReturnPolicy';
import PrivacyPolicy from './pages/PrivacyPolicy';
import BusinessAccount from './pages/BusinessAccount';
import SellerSignup from './pages/SellerSignup';

// Unified Bazar pages
import Bazar from './pages/Bazar';
import SingleProduct from './pages/SingleProduct';

// Admin pages
import { AdminDashboard } from './pages/admin/Dashboard';

// Store Owner pages
import { StoreOwnerDashboard } from './pages/store-owner/Dashboard';

// Support pages
import { SupportDashboard } from './pages/support/Dashboard';

// Employee pages
import { EmployeeDashboard } from './pages/employees/Dashboard';

// Upazila Admin pages
import { UpazilaAdminDashboard } from './pages/upazila-admin/Dashboard';

// Customer pages
import CustomerDashboard from './pages/customer/Dashboard';

// Inventory pages
import InventoryDashboard from './pages/inventory/InventoryDashboard';
import POSSystem from './pages/inventory/POSSystem';

// Import styles
import theme from './styles/theme';
import { ThemeProvider } from '@mui/material/styles';
import { getPageTitle } from './utils/pageTitles';

// Create a separate component for the app content to use the location context
const AppContent = () => {
  const { userLocation, updateLocation, isLoading } = useLocation();
  const [showLocationPopup, setShowLocationPopup] = useState(false);
  const routerLocation = useRouterLocation();
  const [currentLanguage, setCurrentLanguage] = useState((typeof window !== 'undefined' && localStorage.getItem('preferredLanguage')) || 'bn');

  // Check for existing location data on app mount
  useEffect(() => {
    // Wait for loading to complete before showing popup
    if (!isLoading) {
      if (!userLocation) {
        // First time user - show location popup
        setShowLocationPopup(true);
      } else {
        setShowLocationPopup(false);
      }
    }
  }, [userLocation, isLoading]);

  // Handle location selection
  const handleLocationSelected = (locationData) => {
    updateLocation(locationData);
    setShowLocationPopup(false);
  };

  // Update page title on route or language change
  useEffect(() => {
    const title = getPageTitle(routerLocation.pathname, currentLanguage);
    if (title && typeof document !== 'undefined') {
      document.title = title;
    }
  }, [routerLocation.pathname, currentLanguage]);

  // Listen for language change events to update page title without reload
  useEffect(() => {
    const updateLang = () => {
      const lang = (typeof window !== 'undefined' && localStorage.getItem('preferredLanguage')) || 'bn';
      setCurrentLanguage(lang);
    };
    const storageHandler = (e) => {
      if (e.key === 'preferredLanguage') updateLang();
    };
    window.addEventListener('preferredLanguageChanged', updateLang);
    window.addEventListener('storage', storageHandler);
    return () => {
      window.removeEventListener('preferredLanguageChanged', updateLang);
      window.removeEventListener('storage', storageHandler);
    };
  }, []);

  // Check if current page should show emergency service button
  const shouldShowEmergencyButton = () => {
    const pathname = routerLocation.pathname;
    const dashboardRoutes = [
      '/admin/dashboard',
      '/store-owner/dashboard', 
      '/support/dashboard',
      '/employees/dashboard',
      '/upazila-admin/dashboard',
      '/inventory/dashboard'
    ];
    
    return !dashboardRoutes.some(route => pathname.startsWith(route));
  };

  return (
    <>
      {/* Location Selection Popup - appears on all pages */}
      <LocationSelectionPopup
        open={showLocationPopup}
        onLocationSelected={handleLocationSelected}
      />
      
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/help" element={<Help />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/career" element={<Career />} />
        <Route path="/career/job/:jobId" element={<JobDetail />} />
        <Route path="/become-seller" element={<BecomeSeller />} />
        <Route path="/marketplace-policy" element={<MarketplacePolicy />} />
        <Route path="/delivery-policy" element={<DeliveryPolicy />} />
        <Route path="/return-policy" element={<ReturnPolicy />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/business" element={<BusinessAccount />} />
        <Route path="/seller-signup" element={<SellerSignup />} />
        <Route path="/bazars" element={<BazarList />} />
        
        {/* Unified Bazar Routes */}
        <Route path="/daily-bazar" element={<Bazar />} />
        <Route path="/:bazarSlug/product/:id" element={<SingleProduct />} />
        <Route path="/food-bazar" element={<Bazar />} />
        <Route path="/grains" element={<Bazar />} />
        <Route path="/garments-bazar" element={<Bazar />} />
        <Route path="/cosmetics-bazar" element={<Bazar />} />
        <Route path="/book-bazar" element={<Bazar />} />
        <Route path="/nursery-bazar" element={<Bazar />} />
        <Route path="/electronics-bazar" element={<Bazar />} />
        <Route path="/chemical-bazar" element={<Bazar />} />
        <Route path="/hardware-bazar" element={<Bazar />} />
        <Route path="/national-bazar" element={<Bazar />} />
        <Route path="/feed-bazar" element={<Bazar />} />


        
        {/* Keep existing routes for সার্ভিস and ঔষধ */}
        <Route path="/medicine-bazar" element={<MedicineBazar />} />
        <Route path="/medicine-bazar/product/:id" element={<MedicineBazarProductDetail />} />
        <Route path="/service-bazar" element={<ServiceBazar />} />
        
        <Route path="/search" element={<SearchResult />} />
        <Route path="/bazars/:slug" element={<BazarDetail />} />
        <Route path="/stores/:slug" element={<StoreDetail />} />
        <Route path="/products/:id" element={<ProductDetail />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/login" element={<Login />} />
        <Route path="/location-test" element={<LocationTest />} />
        
        {/* Admin Routes */}
        <Route 
          path="/admin/dashboard/*" 
          element={
            // <RoleRoute roles={['admin']}>
              <AdminDashboard />
            // </RoleRoute>
          } 
        />
        
        {/* Store Owner Routes */}
        <Route 
          path="/store-owner/dashboard/*" 
          element={
            // <RoleRoute roles={['store_owner']}>
              <StoreOwnerDashboard />
            // </RoleRoute>
          } 
        />
        
        {/* Support Routes */}
        <Route 
          path="/support/dashboard/*" 
          element={
            // <RoleRoute roles={['support']}>
              <SupportDashboard />
            // </RoleRoute>
          } 
        />
        
        {/* Customer Routes */}
        <Route 
          path="/customer/dashboard/*" 
          element={
            // <RoleRoute roles={['customer']}>
              <CustomerDashboard />
            // </RoleRoute>
          } 
        />
        
        {/* Employee Routes */}
        <Route 
          path="/employees/dashboard/*" 
          element={
            // <RoleRoute roles={['employee']}>
              <EmployeeDashboard />
            // </RoleRoute>
          } 
        />
        
        {/* Upazila Admin Routes */}
        <Route 
          path="/upazila-admin/dashboard/*" 
          element={
            // <RoleRoute roles={['upazila_admin']}>
              <UpazilaAdminDashboard />
            // </RoleRoute>
          } 
        />
        
        {/* Inventory Dashboard Route */}
        <Route 
          path="/inventory/dashboard/*" 
          element={
            <InventoryDashboard />
          } 
        />
        <Route path="/inventory/pos-system/*" element={<POSSystem />} />
        
        {/* Fallback route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      {/* Emergency Service Button - Show on all pages except dashboards */}
      {shouldShowEmergencyButton() && <EmergencyServiceButton />}
      
      {/* Music Player - Show on all pages except dashboards */}
      {shouldShowEmergencyButton() && <MusicPlayer />}
    </>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <CartProvider>
          <LocationProvider>
            <Router>
              <AppContent />
            </Router>
          </LocationProvider>
        </CartProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
